import { useI18n } from "@core/i18n/i18n";
import { ExtendedPatientProgram } from "@domain/program/patient-program";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { useCallback, useMemo, useState } from "react";
import { isWordMatchingSearch } from "@utils/string-utils";
import { useFormatPatients } from "@domain/patient/hooks/use-patients";

interface SearchFilterState {
	search: string;
	filters: ProgramStatus[];
}

export function useProgramFilters(programs: ExtendedPatientProgram[]): {
	allFilters: { label: string; value: ProgramStatus }[];
	setSearchFilter: (state: SearchFilterState) => void;
	programTotalCount: number;
	filteredPrograms: ExtendedPatientProgram[];
} {
	const { format } = useI18n();
	const { formatPatientName } = useFormatPatients();
	const [searchFilter, setSearchFilter] = useState<SearchFilterState>({ search: "", filters: [ProgramStatus.Ongoing] });
	const programTotalCount = useMemo(() => programs.length, [programs]);

	const matchesSearch = useCallback(
		(program: ExtendedPatientProgram, textToSearch: string): boolean =>
			isWordMatchingSearch(program.name, textToSearch) ||
			isWordMatchingSearch(program.patient.firstName, textToSearch) ||
			isWordMatchingSearch(program.patient.lastName, textToSearch) ||
			isWordMatchingSearch(formatPatientName(program.patient), textToSearch),
		[formatPatientName],
	);

	const filteredPrograms = useMemo(() => {
		const hasFilters = searchFilter.filters.length > 0;
		const hasSearch = Boolean(searchFilter.search);

		return hasFilters || hasSearch
			? programs.filter(program => {
					if (hasFilters && !searchFilter.filters.includes(program.status)) {
						return false;
					}

					if (hasSearch && !matchesSearch(program, searchFilter.search)) {
						return false;
					}

					return true;
				})
			: programs;
	}, [matchesSearch, programs, searchFilter.filters, searchFilter.search]);

	const allFilters = useMemo(
		() => [
			{
				label: format("global.programStatus.labels.draft"),
				value: ProgramStatus.Draft,
			},
			{
				label: format("global.programStatus.labels.scheduled"),
				value: ProgramStatus.Scheduled,
			},
			{
				label: format("global.programStatus.labels.ongoing"),
				value: ProgramStatus.Ongoing,
			},
			{
				label: format("global.programStatus.labels.ended"),
				value: ProgramStatus.Ended,
			},
		],
		[format],
	);

	return { allFilters, setSearchFilter, programTotalCount, filteredPrograms };
}
